import { useEffect, useState } from "react";
import { ShieldAlert, FileText, FileSpreadsheet, Loader2, CalendarDays } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtAmount } from "@/lib/render";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const LimitsCard = ({ limits, isAdmin, onDone }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (limits) setVal(String(limits.daily_card_limit)); }, [limits]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/processor-engine/settings", { daily_card_limit: parseFloat(val) });
      toast.success("Daily card limit updated"); onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="pe-limits-card">
      <p className="font-display font-semibold flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-500" />Daily card limit</p>
      <p className="text-sm text-muted-foreground">Max a single card can be charged or deposit per UTC day (Vault charges + card deposits, net of refunds).</p>
      <p data-testid="pe-limit-value" className="font-mono text-xl">{limits ? fmtAmount(limits.daily_card_limit, "USD") : "—"}</p>
      {isAdmin && (
        <div className="flex gap-2">
          <Input data-testid="pe-limit-input" type="number" min="1" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} className="bg-muted/40 border-border font-mono" />
          <Button data-testid="pe-limit-save" onClick={save} disabled={busy || !(parseFloat(val) > 0)} className="bg-indigo-500 hover:bg-indigo-600 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
};

export const StatementCard = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState("");

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get("/processor-engine/statement", { params: { month, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `citidan-processor-statement-${month}.${format}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Statement ${month} downloaded`);
    } catch { toast.error("Could not generate statement"); } finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="pe-statement-card">
      <p className="font-display font-semibold flex items-center gap-2"><CalendarDays className="h-4 w-4 text-indigo-400" />Monthly statement</p>
      <p className="text-sm text-muted-foreground">Every deposit, withdrawal, charge and refund on the Processor account, with opening and closing balance.</p>
      <Input data-testid="pe-statement-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="bg-muted/40 border-border font-mono" />
      <div className="flex gap-2">
        <Button data-testid="pe-statement-pdf" variant="outline" onClick={() => download("pdf")} disabled={!!busy || !month} className="flex-1 border-border">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1" />PDF</>}
        </Button>
        <Button data-testid="pe-statement-csv" variant="outline" onClick={() => download("csv")} disabled={!!busy || !month} className="flex-1 border-border">
          {busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1" />CSV</>}
        </Button>
      </div>
    </div>
  );
};
